import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Puzzle, PuzzleConstants} from './puzzle.js';
import {BoardObject} from './board-object.js';
import {FailStateNames} from '../enums/FailStateNames.js';
import {DropMode} from './puzzle-camera.js';
import {vec3} from 'gl-matrix';
import {Mathfx} from '../utils/mathFx.js';
import {AnimationController} from '@wonderlandengine/tweenjs';
import {RNG} from '../utils/rng.js';

// Animations
import IdleAnimation from './animations/Mal@idle.json' assert {type: 'json'};
import BlinkAnimation from './animations/Mal@blink.json' assert {type: 'json'};
import LoadAnimation from './animations/Mal@load.json' assert {type: 'json'};
import DropAnimation from './animations/Mal@drop.json' assert {type: 'json'};
import {WLUtils} from '../utils/WLUtils.js';
import {GameFlowController} from './game-flow-controller.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {Sounds} from '../enums/Sounds.js';
import {AudioDirector} from './audio-director.js';

export enum MalState {
    Idle,
    DropStart,
    DropEnd,
    Loading,
    Dying,
}

enum FruitAnimationState {
    start,
    loop,
    end,
}

const tempVec = vec3.create();

//TK: Because the various death animations are not implemented yet, and we need some delay.
// This can be removed after the death animations are implemented.
const TEMP_DELAY_AFTER_DEATH = 1.0;

export class MalAnimation extends Component {
    static TypeName = 'mal-animation';

    @property.vector3()
    pieceOffset = [0, 0, 0];

    @property.float(1)
    minimumDropDelay = 1;

    @property.float(0.3)
    pieceDropDelay = 0.3;

    @property.float(0.2)
    pieceAppearDelay = 0.2;

    @property.float(1.25)
    launchHeight = 1.25;

    @property.float(0.1)
    launchUpTime = 0.1;

    @property.float(5.0)
    minimumBlinkDelay = 5.0;

    @property.float(10.0)
    maximumBlinkDelay = 10.0;

    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    launchSFX: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    duckDownSFX: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    sparkleSFX: Sounds = Sounds.none;

    private _state: MalState = MalState.Idle;
    private _puzzle: Puzzle;
    private _nextFruit: Object3D;
    private _dropPending: boolean;

    private _causeOfDeath = FailStateNames.None;

    private _time = 0;
    private _timeSinceDrop: number;
    private _lastDropTime: number = 0;
    private _timerStart: number;
    private _currentBlinkDelay: number = 0;
    private _fruitLoaded: boolean;
    private _animatingFruit: boolean;
    private _fruitAnimationState: FruitAnimationState;
    private _currentAnimatingFruit: Object3D;
    private _currentAnimatingFruit_offset: number;
    private _currentAnimatingFruit_startTime: number;
    private _currentAnimatingFruit_initalY: number;
    private _currentAnimatingFruit_elapsed: number;
    private _animator: AnimationController;

    private _coroutineManager = new CoroutineManager();

    init() {
        this._animatingFruit = false;
        this._fruitLoaded = false;
        this._dropPending = false;
        this._nextFruit = null;
    }

    start() {
        this._puzzle = Puzzle.instance;

        this._animator =
            this.object.getComponent(AnimationController) ??
            this.object.addComponent(AnimationController);

        this._animator.addClip(BlinkAnimation, 'blink');
        this._animator.addClip(IdleAnimation, 'idle');
        this._animator.addClip(LoadAnimation, 'load');
        this._animator.addClip(DropAnimation, 'drop');
        // this._animator.addClip(worryAnimation, "worry");
        // this._animator.addClip(victoryAnimation1, "victory1");
        // this._animator.addClip(victoryAnimation2, "victory2");
        // this._animator.addClip(victoryAnimation3, "victory3");

        this._animator.setLayer('blink', 2);
        //this._animator.setLayer('worry', 1);

        this._animator.play('idle');
    }

    update(dt: number) {
        let elapsed: number;
        this._time += dt;

        this._coroutineManager.update(dt);

        if (this._animatingFruit) {
            this._animateFruit(dt);
        }

        switch (this._state) {
            case MalState.DropStart:
                elapsed = this._time - this._timerStart;
                if (elapsed > this.pieceDropDelay) {
                    this._launchCurrentFruit();
                    this._state = MalState.DropEnd;

                    AudioDirector.instance.playOneShot(this.launchSFX, 0.5, true);
                }
                break;
            case MalState.DropEnd:
                elapsed = this._time - this._timerStart;
                if (elapsed > this._animator.getLength('drop')) {
                    this._state = MalState.Idle;
                    this.load();
                }
                break;
            case MalState.Loading:
                elapsed = this._time - this._timerStart;
                if (elapsed > this.pieceAppearDelay) {
                    //Logger.Assert(nextFruit == null, "Fruit drop race condition!");
                    this._nextFruit = this._puzzle.spawner.queueTop();
                    WLUtils.setActive(this._nextFruit, true);
                    this._puzzle.spawner.popNext();
                    this._fruitLoaded = true;
                    this._state = MalState.Idle;

                    AudioDirector.instance.playOneShot(this.duckDownSFX, 0.5, true);
                }
                break;
            case MalState.Idle:
                this._timeSinceDrop = this._time - this._lastDropTime;
                if (this._causeOfDeath != FailStateNames.None) {
                    this._startDeath();
                } else if (
                    this._dropPending &&
                    this._fruitLoaded &&
                    (this._timeSinceDrop > this.minimumDropDelay ||
                        this._puzzle.mainCamera.currentDropMode == DropMode.Immediate)
                ) {
                    this._startDrop();
                }
                break;
            case MalState.Dying:
                break;
        }

        if (this._time > this._currentBlinkDelay && this._state != MalState.Dying) {
            this._animator.play('blink');
            this._currentBlinkDelay =
                this._time + RNG.getUniform(this.minimumBlinkDelay, this.maximumBlinkDelay);
        }
    }

    /**
     * Originally this was a coroutine in Unity, but we can't do that here.
     * Hence the odd construction.
     */
    private _animateFruit(dt: number = 0) {
        switch (this._fruitAnimationState) {
            case FruitAnimationState.start:
                this._fruitAnimationState = FruitAnimationState.loop;

                // animate the fruit up
                this._currentAnimatingFruit_offset = 0.0;
                this._currentAnimatingFruit_startTime = this._time;
                this._currentAnimatingFruit.getPositionWorld(tempVec);
                this._currentAnimatingFruit_initalY = tempVec[1];
                this._currentAnimatingFruit_elapsed = 0.0;

                this._currentAnimatingFruit.parent = null;
                this._currentAnimatingFruit.resetScaling();

                break;
            case FruitAnimationState.loop:
                this._currentAnimatingFruit_elapsed =
                    this._time - this._currentAnimatingFruit_startTime;
                const percentComplete =
                    this._currentAnimatingFruit_elapsed / this.launchUpTime;

                const offset = Mathfx.sinerp(0.0, this.launchHeight, percentComplete);

                this._currentAnimatingFruit.getPositionWorld(tempVec);
                tempVec[1] = this._currentAnimatingFruit_initalY + offset;
                this._currentAnimatingFruit.setPositionWorld(tempVec);

                if (this._currentAnimatingFruit_elapsed > this.launchUpTime) {
                    this._fruitAnimationState = FruitAnimationState.end;
                }
                break;
            case FruitAnimationState.end:
                const boardObject = this._currentAnimatingFruit.getComponent(BoardObject);
                const laneIndex = this._puzzle.mainCamera.findClosestLane();
                boardObject.drop(this._puzzle, -1, laneIndex);

                this._animatingFruit = false;
                break;
        }
    }

    private _launchCurrentFruit() {
        if (this._nextFruit) {
            this._fruitLoaded = false;

            this._animatingFruit = true;
            this._fruitAnimationState = FruitAnimationState.start;
            this._currentAnimatingFruit = this._nextFruit;
            this._animateFruit(); // trigger first sequence.

            this._nextFruit = null;
            this._lastDropTime = this._time;
        }
    }
    private _startDrop() {
        this._animator.play('drop');
        this._animator.playQueued('idle');

        this._state = MalState.DropStart;
        this._timerStart = this._time;
        this._dropPending = false;
    }

    private _startDeath() {
        this._state = MalState.Dying;
        if (this._fruitLoaded && this._nextFruit) {
            const boardComponent = this._nextFruit.getComponent(BoardObject);
            boardComponent.pop(0, 0, false);
            this._nextFruit = null;
        }

        switch (this._causeOfDeath) {
            case FailStateNames.BlobsOverflow:
                //   StartCoroutine("DoAbsorbedDeath");
                this._coroutineManager.addCoroutine(new Coroutine(this._doAbsorbedDeath()));
                break;

            case FailStateNames.FruitOverflow:
                //   StartCoroutine("DoFlyingDeath");
                this._coroutineManager.addCoroutine(new Coroutine(this._doFlyingDeath()));
                break;

            case FailStateNames.TimeUp:
                //   StartCoroutine("DoTimeUpDeath");
                break;

            case FailStateNames.CountdownExpired:
                //   StartCoroutine("DoCursedDeath");
                break;

            case FailStateNames.MaxLevelReached:
                this._coroutineManager.addCoroutine(
                    new Coroutine(this._doReachedMaxLevel())
                );
                break;

            case FailStateNames.TutorialWon:
                //   StartCoroutine("DoBeatTutorial");
                break;
        }
    }

    die(cause: FailStateNames) {
        this._causeOfDeath = cause;
    }

    drop() {
        this._dropPending = true;
    }

    resetMal() {
        this._state = MalState.Idle;
        this._animator.play('idle');
        this._timerStart = 0.0;
        this._animatingFruit = false;
        this._fruitLoaded = false;
        this._dropPending = false;
        this._causeOfDeath = FailStateNames.None;
        //Utils.SetActiveRecursively(malRendererRoot, true);
        if (this._nextFruit) {
            this._nextFruit.destroy();
            this._nextFruit = null;
        }
        this._lastDropTime = 0;
        // MalFacialAnimation facial = gameObject.GetComponent<MalFacialAnimation>();
        // if (facial != null)
        // {
        //     facial.SetVictorious(false);
        // }
    }

    load() {
        if (this._state == MalState.Idle || this._state == MalState.Loading) {
            const queueNext = this._puzzle.spawner.queueTop();
            if (queueNext && queueNext != this._nextFruit) {
                if (this._nextFruit && this._nextFruit != queueNext) {
                    // we're going to return the currently loaded fruit to the stack.
                    this._puzzle.spawner.returnToQueue(this._nextFruit);
                    this._nextFruit = null;
                }

                // if we're already in the loading state that means that you can't see the
                // fruit yet, so we can just swap it out and continue.
                if (this._state == MalState.Idle) {
                    this._animator.stop('load');
                    this._animator.play('load');
                    this._animator.playQueued('idle');
                    this._timerStart = this._time;
                }
                this._state = MalState.Loading;

                queueNext.parent = this.object;
                queueNext.setPositionLocal(this.pieceOffset);

                const boardComponent = queueNext.getComponent(BoardObject);
                boardComponent.pause();
            }
        }
    }

    private *_doAbsorbedDeath() {
        // if (animator.IsPlaying("blink"))
        //     {
        //         animator.Stop("blink");
        //     }
        //     MalFacialAnimation facial = GetComponentInChildren<MalFacialAnimation>();
        //     facial.SetFailed(true);
        //     yield return null;

        const columnCount = this._puzzle.lanes.length;
        const topRow = PuzzleConstants.rowCount - 1;
        let blob: BoardObject = null;
        let maxDelay = 0.0;

        // let hopObjectList: BoardObject[] = [];
        // let hopDelayList: number[] = [];
        // for (let x = 0; x < columnCount; x++) {
        //     const obj = this._puzzle.getObject(topRow, x);
        //     if (obj && !obj.isFruit) {
        //         if (blob == null) {
        //             blob = obj;
        //         } else {
        //             const delay = RNG.getUniform(0.05 * x - 1, 1.0);
        //             if (delay > maxDelay) {
        //                 maxDelay = delay;
        //             }
        //             hopObjectList.push(obj);
        //             hopDelayList.push(delay);
        //         }
        //         obj.object.parent = this._puzzle.object;
        //     } else if (obj && obj.isFruit) {
        //         obj.pop(0, 0, false);
        //     }
        // }
        //  for (let i = 0; i < hopObjectList.length; i++)
        //  {
        //      hopObjectList[i].hopOnMal(null, hopDelayList[i], 0.0f, Quaternion.identity, 1.0f, maxDelay + 0.2f + absorbDeathBounceUpAfterSeconds);
        //  }

        //     Vector3 relativePos = transform.position - Camera.main.transform.position;
        //     relativePos.y = 0.0f;

        //     Quaternion rotation = Quaternion.LookRotation(relativePos);

        //     // scale between absorbDeathMinScale and absorbDeathMaxBlobScale based on number of blobs jumping
        //     float blobScale = (Mathf.Min(hopObjectList.Count / absorbDeathMaxBlobs, 1.0f)) * (absorbDeathMaxBlobScale - absorbDeathMinBlobScale) + absorbDeathMinBlobScale;

        //     // copyOfMal will be destroyed by the blob
        //     GameObject copyOfMal = CopyMal(malRendererRoot, transform.position, Quaternion.identity);
        //     Utils.SetActiveRecursively(copyOfMal, false);
        //     BounceUpData bounce = new BounceUpData();
        //     bounce.copyOfMal = copyOfMal;
        //     bounce.blobShader = absorbDeathBlobShader;
        //     bounce.transparency = absorbDeathTransparency;
        //     bounce.scaleAdjustment = absorbDeathScaleAdjustment;
        //     bounce.heightAdjustment = absorbDeathHeightAdjustment;
        //     blob.HopOnMal(bounce, maxDelay + 0.2f, absorbDeathBounceUpAfterSeconds, rotation, blobScale, 0.0f);

        //     yield return new WaitForSeconds(absorbDeathMalReactionSeconds);
        //     animator.Play("load");
        //     yield return new WaitForSeconds(absorbDeathReplaceMalAfterSeconds);
        //     Utils.SetActiveRecursively(malRendererRoot, false);
        yield* waitForSeconds(maxDelay + 0.2 + TEMP_DELAY_AFTER_DEATH);
        GameFlowController.instance.gameOver();
        //     facial.SetFailed(false);
    }

    private *_doFlyingDeath() {
        yield* waitForSeconds(TEMP_DELAY_AFTER_DEATH);
        GameFlowController.instance.gameOver();
    }

    private *_doReachedMaxLevel() {
        // SetWorried(false);
        // int animation = Random.Range(1, victoryAnimationCount + 1);
        // animator.Play("victory" + animation);
        // MalFacialAnimation facial = gameObject.GetComponent<MalFacialAnimation>();
        // if (facial != null)
        // {
        // 	facial.SetVictorious(true);
        // }
        // yield return new WaitForSeconds(reachedMaxLevelFinishSeconds + puzzle.delayForVictoryExplosion);
        // GameFlowController gameFlow = GameFlowController.GetInstance();
        // gameFlow.GameOver();
        // while (gameFlow.Transition.TransitionInProgress)
        // {
        // 	yield return null;
        // }
        // if (facial != null)
        // {
        // 	facial.SetVictorious(false);
        // }
        yield* waitForSeconds(3);
        GameFlowController.instance.gameOver();
    }
}
